/**
 * THE FOUR QUADRANTS, AND THE ONE PLACE THEIR CHIP COLOUR IS DECIDED.
 *
 * §6.5 names four quadrants and gives them a priority order. It gives them no colours, and
 * **this module's `scoring_config` has no colour column** — which is the difference from APR,
 * where `CLAUDE.md` §1.9.3.3 can say "disposition colour comes from `apr.scoring_thresholds`
 * and from nowhere else" because that table stores one. D-004 part 1 forbids reading APR's,
 * and `pages/apr/dispositionPalette.ts` is in an APR product folder, so D-003 forbids importing
 * it even if the vocabulary matched — which it does not: APR's four words are dispositions for
 * one application, these four are quadrants for a BU × system × area.
 *
 * SO THE COLOUR HAD TO COME FROM SOMEWHERE, AND IT COMES FROM THE MEASURED SET.
 * Every pair below is one of `CLAUDE.md` §1.9.2's five, already in `chips.ts` with its computed
 * contrast ratio — *"use these values; do not invent a neighbouring shade."* Nothing here is a
 * new colour. What IS a decision is WHICH measured pair each quadrant wears, and that decision
 * is recorded in `docs/consolidation/UI_GAPS.md` for the owner rather than left implicit in a
 * screen: it is four lines to change here and it changes every screen at once, which is the
 * property §1.9.3.3 is really asking for.
 *
 * WHY ONE FILE AND NOT A LITERAL PER SCREEN. The scores list, the assessment grid and the
 * calculation breakdown all name a quadrant. Three copies of the mapping is three chances to
 * drift, and the fix for that is the same one APR reached for — one module, imported.
 *
 * `QUADRANT_ORDER` is NOT redeclared here. It is `@platform/erp-scoring`'s, which is what the
 * server filters and ranks by, so a screen listing the four names imports that and gets §6.5's
 * order rather than this file's key order.
 */

import { type QuadrantName } from '@platform/erp-scoring';
import { CHIP_INFO, CHIP_NEUTRAL, CHIP_SUCCESS, CHIP_WARNING, type ChipPair } from './chips';

/**
 * The pair each quadrant wears, and the sentence the screen puts in its `title`.
 *
 * The `meaning` strings are §6.5's own two conditions restated for one quadrant — high or low
 * overlap against the initiative's overlap threshold, high or low complexity against its
 * complexity threshold. The breakdown endpoint returns the full rule as prose
 * (`breakdown.quadrant.rule`), and where a screen has that it renders the server's sentence
 * instead of this one; this is for the places that have only a name, such as a table cell.
 */
export const QUADRANT_CHIP: Record<QuadrantName, { pair: ChipPair; meaning: string }> = {
  /* High overlap, low complexity — the thing worth doing first. The success pair, 6.34:1. */
  'Quick win': {
    pair: CHIP_SUCCESS,
    meaning: 'High overlap, low complexity — consolidate this first.',
  },
  /* High overlap AND high complexity: worth doing, and a programme rather than a task. 7.04:1. */
  'Strategic move': {
    pair: CHIP_INFO,
    meaning: 'High overlap and high complexity — worth doing, and it needs a plan.',
  },
  /* Low overlap, high complexity — the one that needs work before it can be judged. 6.57:1. */
  'Harmonize first': {
    pair: CHIP_WARNING,
    meaning: 'Low overlap, high complexity — harmonize the process before consolidating.',
  },
  /* Low overlap, low complexity — no case either way. The neutral pair, 8.68:1. */
  'Defer or retain': {
    pair: CHIP_NEUTRAL,
    meaning: 'Low overlap, low complexity — little to gain; defer or retain.',
  },
};
